import { Currency, type Dtos, formatToDollar, pluralize } from "@inrev/common";
import { Option } from "@inrev/core";
import { DateTime } from "luxon";
import { match } from "ts-pattern";
import { formatEmpty } from "../../../utils";

export type IndividualCreditReport = Dtos.CreditReport.Individual.Admin.Response;
export type IndividualCreditReportFailure = Dtos.CreditReport.Individual.Admin.Failure;
export type ExperianCreditDetail = Dtos.CreditReport.Individual.Admin.Experian.Detail;
export type ExperianTradeline = Dtos.CreditReport.Individual.Admin.Experian.Tradeline;
export type ExperianPaymentMonth = Dtos.CreditReport.Individual.Admin.Experian.PaymentMonth;
type ExperianTradelineCategory = Dtos.CreditReport.Individual.Admin.Experian.TradelineCategory;

export const FICO_MIN = 300;
export const FICO_MAX = 850;

type ScoreBandName = "Poor" | "Fair" | "Good" | "Very good" | "Exceptional";

export type ScoreBand = {
	name: ScoreBandName;
	min: number;
	max: number;
	/** `chart-*` token painting the band's slice of the range bar. */
	chartToken: string;
	/** Semantic color for the score text and band label. */
	tone: "danger" | "warning" | "confirm";
};

/** Standard FICO ranges. The app had no band logic before this modal. */
export const SCORE_BANDS: readonly ScoreBand[] = [
	{ name: "Poor", min: FICO_MIN, max: 579, chartToken: "bg-chart-danger-700", tone: "danger" },
	{ name: "Fair", min: 580, max: 669, chartToken: "bg-chart-amber-600", tone: "warning" },
	{ name: "Good", min: 670, max: 739, chartToken: "bg-chart-warning-300", tone: "confirm" },
	{ name: "Very good", min: 740, max: 799, chartToken: "bg-chart-success-500", tone: "confirm" },
	{
		name: "Exceptional",
		min: 800,
		max: FICO_MAX,
		chartToken: "bg-chart-success-700",
		tone: "confirm",
	},
];

const clampScore = (score: number): number => Math.min(FICO_MAX, Math.max(FICO_MIN, score));

export const scoreBand = (score: number): ScoreBand =>
	SCORE_BANDS.find((band) => clampScore(score) <= band.max) ?? SCORE_BANDS[SCORE_BANDS.length - 1];

/** Share of the 300–850 range a band covers, for flex-sizing the range bar. */
export const bandWidthPct = (band: ScoreBand): number =>
	((band.max - band.min + 1) / (FICO_MAX - FICO_MIN + 1)) * 100;

export const scorePositionPct = (score: number): number =>
	((clampScore(score) - FICO_MIN) / (FICO_MAX - FICO_MIN)) * 100;

/** "8 pts below Good", or undefined at the top band. */
export const pointsToNextBand = (score: number): string | undefined => {
	const index = SCORE_BANDS.indexOf(scoreBand(score));
	const next = SCORE_BANDS[index + 1];
	return Option.isSome(next)
		? `${pluralize(next.min - clampScore(score), "pt", "pts")} below ${next.name}`
		: undefined;
};

export const formatReportDate = (iso: string | undefined): string =>
	formatEmpty(Option.map(iso, (date) => DateTime.fromISO(date).toFormat("MM/dd/yyyy")));

export const formatMonthYear = (iso: string | undefined): string =>
	formatEmpty(Option.map(iso, (date) => DateTime.fromISO(date).toFormat("MM/yyyy")));

export const formatReportDollars = (value: number | undefined): string | undefined =>
	formatToDollar(value, true) ?? undefined;

/** "12 yrs 3 mo" from the oldest opened date to `now`. */
export const formatCreditAge = (oldestOpenedOn: string | undefined, now = DateTime.now()) => {
	if (Option.isNone(oldestOpenedOn)) return undefined;
	const { years, months } = now
		.diff(DateTime.fromISO(oldestOpenedOn), ["years", "months"])
		.shiftTo("years", "months");
	return `${Math.floor(years)} yrs ${Math.floor(months)} mo`;
};

export const vendorLabel = (vendor: IndividualCreditReport["vendor"]): string =>
	match(vendor)
		.with("experian", () => "Experian")
		.with("meridian", () => "Equifax via Meridian")
		.exhaustive();

export const bureauLabel = (vendor: IndividualCreditReport["vendor"]): string =>
	match(vendor)
		.with("experian", () => "Experian")
		.with("meridian", () => "Equifax")
		.exhaustive();

const MISSING_FIELD_LABELS: Record<
	NonNullable<
		Extract<IndividualCreditReportFailure, { reason: "insufficient_data" }>["missing"]
	>[number],
	string
> = {
	name: "full name",
	address: "complete address",
	ssn: "SSN",
};

const joinWords = (words: string[]): string =>
	words.length <= 1
		? (words[0] ?? "")
		: `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;

export type FailureCopy = {
	/** Short label for the "Failure reason" value. */
	label: string;
	/** Banner explanation of what went wrong and what to do next. */
	message: string;
	tone: "info" | "danger";
};

export const failureCopy = (
	failure: IndividualCreditReportFailure,
	vendor: IndividualCreditReport["vendor"],
): FailureCopy => {
	const bureau = bureauLabel(vendor);
	return match(failure)
		.with({ reason: "frozen" }, () => ({
			label: "Frozen file",
			message: `${bureau} blocked this pull because the owner has a security freeze on their credit file. Ask them to lift the freeze temporarily with ${bureau}, then pull the report again.`,
			tone: "info" as const,
		}))
		.with({ reason: "not_found" }, () => ({
			label: "File not found",
			message: `${bureau} has no credit file matching the name, address and SSN we sent. Check the owner's details for typos or a recent move, then pull the report again.`,
			tone: "danger" as const,
		}))
		.with({ reason: "no_score" }, () => ({
			label: "No score",
			message: `${bureau} found the owner's credit file but couldn't return a score, usually because the file is too thin or too new to score.`,
			tone: "danger" as const,
		}))
		.with({ reason: "insufficient_data" }, ({ missing }) => ({
			label: "Insufficient data",
			message:
				Option.isSome(missing) && missing.length > 0
					? `We couldn't pull a report because the owner is missing their ${joinWords(missing.map((field) => MISSING_FIELD_LABELS[field]))}. Add the missing details to the owner, then pull the report again.`
					: "We couldn't pull a report because the owner's name, address or SSN is missing. Add the missing details to the owner, then pull the report again.",
			tone: "danger" as const,
		}))
		.with({ reason: "vendor_error" }, ({ statusCode, vendorCode }) => {
			const reference = [
				Option.isSome(statusCode) ? `HTTP ${statusCode}` : undefined,
				Option.isSome(vendorCode) ? `${bureau} code ${vendorCode}` : undefined,
			].filter(Option.isSome);
			return {
				label: "Vendor error",
				message: `${bureau} returned an error, so no report was created. The problem was on ${bureau}'s side, not with the owner's information. Try the pull again; if it keeps failing, contact support${reference.length > 0 ? ` and share ${reference.join(", ")}` : ""}.`,
				tone: "danger" as const,
			};
		})
		.with({ reason: "unknown" }, () => ({
			label: "Unknown",
			message: `The ${bureau} pull failed and no reason was recorded. Try the pull again; if it keeps failing, contact support.`,
			tone: "danger" as const,
		}))
		.exhaustive();
};

export const FULL_REPORT_SECTIONS = [
	"Score factors",
	"Public records & legal",
	"Credit summary",
	"Tradelines",
	"Hard inquiries",
	"Identity & stability",
] as const;

export const derogatoryCount = (detail: ExperianCreditDetail): number => {
	const { bankruptcies, liens, judgments, collections } = detail.publicRecords;
	return bankruptcies.length + liens.length + judgments.length + collections.length;
};

export const totalPastDue = (tradelines: ExperianTradeline[]): Currency =>
	Currency.sum(tradelines.map((tradeline) => tradeline.pastDue).filter(Option.isSome));

/** Charge-off and collection months count too: they started as missed payments. */
const isLatePayment = (month: ExperianPaymentMonth): boolean =>
	month === "late_30" || month === "late_60" || month === "late_90_plus" || month === "derogatory";

/** The months a tradeline's history covers, oldest first. It ends at the bureau's last update. */
export const paymentHistoryRange = (
	tradeline: ExperianTradeline,
): Option<{ from: DateTime; through: DateTime }> =>
	Option.map(tradeline.paymentHistoryThrough, (iso) => {
		const through = DateTime.fromISO(iso).startOf("month");
		return { from: through.minus({ months: tradeline.paymentHistory.length - 1 }), through };
	});

const latesWithin = (tradeline: ExperianTradeline, cutoff: DateTime): number => {
	const range = paymentHistoryRange(tradeline);
	return tradeline.paymentHistory.filter(
		(month, index) =>
			isLatePayment(month) &&
			(Option.isNone(range) || range.from.plus({ months: index }) >= cutoff),
	).length;
};

/** Late months in the 24 months up to `asOf`. Undated histories count in full. */
export const latePaymentCount = (tradelines: ExperianTradeline[], asOf: DateTime): number => {
	const cutoff = asOf.startOf("month").minus({ months: 23 });
	return tradelines.reduce((total, tradeline) => total + latesWithin(tradeline, cutoff), 0);
};

/** Inquiries in the `months` up to `asOf`, so an old report keeps the count it had when pulled. */
export const inquiriesWithinMonths = (
	inquiries: ExperianCreditDetail["inquiries"],
	months: number,
	asOf: DateTime,
): number => {
	const cutoff = asOf.minus({ months });
	return inquiries.filter(
		(inquiry) => Option.isSome(inquiry.date) && DateTime.fromISO(inquiry.date) >= cutoff,
	).length;
};

export const categoryLabel = (category: ExperianTradelineCategory): string =>
	match(category)
		.with("revolving", () => "Revolving")
		.with("installment", () => "Installment")
		.with("mortgage", () => "Real estate")
		.with("collection", () => "Collection")
		.with("other", () => "Other")
		.exhaustive();

export const paymentHistoryLabel = (history: ExperianPaymentMonth[]): string => {
	const reported = history.filter((month) => month !== "no_data");
	const late = reported.filter(isLatePayment).length;
	return late === 0
		? `${reported.length} of ${reported.length} reported months on time`
		: `${pluralize(late, "late payment", "late payments")} in ${pluralize(reported.length, "reported month", "reported months")}`;
};
